import type { IAPAnalytics } from './analytics.types';
import StateObserver from 'src/StateObserver';
import { analytics } from '@play-co/gcinstant';
import { updateSessionRevenueSequence } from 'src/state/analytics';

/**
 * Tracks revenue for in-app purchases and updates relevant sequences.
 *
 * @param opts - The options for tracking revenue.
 * @param opts.revenueType - The type of revenue, which should be 'in_app_purchase'.
 * @param opts.revenueGrossLocal - The gross revenue in local currency.
 * @param opts.currencyCodeLocal - The local currency code.
 * @param opts.dollarToLocalRate - The exchange rate from dollar to local currency.
 * @param opts.paidGems - The number of paid gems.
 * @param opts.freeGems - The number of free gems.
 * @param opts.purchaseCountBefore - The purchase count before the current purchase.
 * @param opts.feature - The feature associated with the revenue.
 *
 * This function performs the following steps:
 * 1. Calculates the absolute in-app purchase count, considering optimistic updates.
 * 2. Pushes the revenue data to the analytics system.
 * 3. Updates the absolute revenue sequence in the state observer.
 * 4. Updates the session revenue sequence in the Redux store.
 */
export function trackRevenue(
  opts: IAPAnalytics & {
    revenueType: 'in_app_purchase';
    revenueGrossLocal: number;
    currencyCodeLocal: string;
    dollarToLocalRate: number;
    paidGems: number;
    freeGems: number;
  },
) {
  const purchaseCountBefore = opts.purchaseCountBefore;
  const revenueType = opts.revenueType;
  const feature = opts.feature;
  const purchaseCountAfter =
    StateObserver.replicant.getPurchaseHistory().length;

  /**
   * Some platform do not add items to purchase history instantly,
   * Here we doing optimistic update of purchase count to avoid having absoluteIapCount === 0 after purchase.
   *
   * We take the maximum of purchaseCountBefore + 1 and purchaseCountAfter,
   * because some platforms and in some cases can have correct number of purchases in purchase history.
   */
  const absoluteIapCount = Math.max(
    purchaseCountBefore + 1,
    purchaseCountAfter,
  );

  const revenueIsTrackedByGCInstant =
    opts.purchaseProvider === 'apple' || opts.purchaseProvider === 'google';

  if (!revenueIsTrackedByGCInstant) {
    // Send Revenue event
    analytics.pushPurchaseRevenue({
      analytics: {
        feature: opts.feature,
        subFeature: opts.subFeature,
        purchaseCountBefore: opts.purchaseCountBefore,
        absoluteIapSequence: absoluteIapCount,
      },
      currency: opts.currencyCodeLocal,
      price: opts.revenueGrossLocal,
      productID: opts.productID,
      provider: opts.purchaseProvider,
      providerMethod: opts.purchaseProviderMethod,
      providerCreditCardBrand: opts.purchaseProviderCreditCardBrand,
      purchaseID: opts.purchaseID,
    });
  }

  // Push absolute revenue sequences into replicant
  void StateObserver.invoke.updateAbsoluteRevenueSequence({
    revenueType,
    feature,
  });

  // Update session sequences on redux
  StateObserver.dispatch(
    updateSessionRevenueSequence({ revenueType, feature }),
  );
}
